import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getTask,
  updateTask,
  deleteTask,
  EventTaskDetail,
  EventTaskAssignment,
  UpdateEventTaskPayload,
} from '../api/event-tasks';
import { apiClient } from '../api/client';

// ── Design Tokens ─────────────────────────────────────────────

const PRIMARY = '#1a6b3c';
const TEXT_DARK = '#0f2d1f';
const TEXT_MUTED = '#6b7280';
const BORDER_LIGHT = '#e5e7eb';
const BG_PAGE = '#f9fafb';
const BG_CARD = '#ffffff';

const STATUS_COLORS: Record<string, string> = {
  ToDo: '#6b7280',
  InProgress: '#f57c00',
  Done: '#1a6b3c',
  Cancelled: '#d32f2f',
};

const STATUS_LABELS: Record<string, string> = {
  ToDo: 'To Do',
  InProgress: 'In Progress',
  Done: 'Done',
  Cancelled: 'Cancelled',
};

const PRIORITY_COLORS: Record<string, string> = {
  High: '#d32f2f',
  Medium: '#f57c00',
  Low: '#6b7280',
};

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '900px', margin: '0 auto', backgroundColor: BG_PAGE, minHeight: '100vh' },
  backLink: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: PRIMARY, fontSize: '0.85rem', textDecoration: 'none', cursor: 'pointer', marginBottom: '1.25rem', border: 'none', background: 'none', fontWeight: 500 },
  card: { backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', padding: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' as const },
  title: { fontSize: '1.5rem', color: TEXT_DARK, margin: 0, fontWeight: 700 },
  metaRow: { display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' as const, marginBottom: '1.5rem' },
  badge: { display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, color: '#fff' },
  metaLabel: { fontSize: '0.75rem', color: TEXT_MUTED, fontWeight: 500 },
  section: { marginBottom: '1.5rem' },
  sectionTitle: { fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.5rem', borderBottom: `1px solid ${BORDER_LIGHT}`, paddingBottom: '0.4rem' },
  fieldGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  fieldGroup: { marginBottom: '1rem' },
  label: { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#333', marginBottom: '4px' },
  value: { fontSize: '0.9rem', color: '#333' },
  input: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const },
  textarea: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const, minHeight: '100px', resize: 'vertical' as const },
  formSelect: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: '#fff' },
  assigneeRow: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0', borderBottom: `1px solid ${BORDER_LIGHT}`, flexWrap: 'wrap' as const },
  assigneeName: { fontWeight: 500, fontSize: '0.9rem', minWidth: '140px' },
  roleSelect: { fontSize: '0.8rem', padding: '3px 8px', borderRadius: '4px', border: '1px solid #ccc', maxWidth: '180px' },
  removeBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#dc2626', padding: '2px 6px' },
  addRow: { display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' as const, alignItems: 'center' },
  actions: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2rem', paddingTop: '1.5rem', borderTop: `1px solid ${BORDER_LIGHT}` },
  btnPrimary: { padding: '0.6rem 1.5rem', backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '0.6rem 1.5rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  btnDanger: { padding: '0.6rem 1.5rem', backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' },
  loading: { textAlign: 'center' as const, padding: '4rem', color: TEXT_MUTED, fontSize: '1rem' },
  timestamps: { fontSize: '0.75rem', color: TEXT_MUTED, marginTop: '1rem' },
};

// ── Helpers ───────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function formatShortDate(dateStr: string): string {
  if (!dateStr) return '';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTimestamp(isoStr: string): string {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleString();
}

// ── Component ─────────────────────────────────────────────────

export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [task, setTask] = useState<EventTaskDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  // Editable fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [category, setCategory] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [eventId, setEventId] = useState('');
  const [fulfillmentComments, setFulfillmentComments] = useState('');

  // Reference data
  const [events, setEvents] = useState<{ id: string; name: string }[]>([]);
  const [weekendTaskRoles, setWeekendTaskRoles] = useState<{ id: string; name: string }[]>([]);
  const [subcommitteeMembers, setSubcommitteeMembers] = useState<{ id: string; firstName: string; lastName: string }[]>([]);

  // Assignee management
  const [assignees, setAssignees] = useState<EventTaskAssignment[]>([]);
  const [addMemberId, setAddMemberId] = useState('');
  const [addRoleId, setAddRoleId] = useState('');
  const [assigningMember, setAssigningMember] = useState(false);

  useEffect(() => {
    if (id) loadTask(id);
  }, [id]);

  async function loadTask(taskId: string) {
    if (!task) setLoading(true);
    setError('');
    try {
      const data = await getTask(taskId);
      setTask(data);
      populateForm(data);
      setAssignees(data.assignees || []);
      loadReferenceData(data.subcommittee?.id);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load task.');
    } finally {
      setLoading(false);
    }
  }

  function populateForm(t: EventTaskDetail) {
    setTitle(t.title);
    setDescription(t.description || '');
    setStatus(t.status);
    setPriority(t.priority);
    setCategory(t.category);
    setDueDate(t.dueDate ? t.dueDate.split('T')[0] : '');
    setEventId(t.event?.id || '');
    setFulfillmentComments(t.fulfillmentComments || '');
  }

  async function loadReferenceData(subcommitteeId?: string) {
    apiClient<{ id: string; name: string }[]>('/events')
      .then((data) => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setEvents([]));

    apiClient<{ id: string; name: string }[]>('/weekend-task-roles')
      .then((data) => setWeekendTaskRoles(Array.isArray(data) ? data : []))
      .catch(() => setWeekendTaskRoles([]));

    if (subcommitteeId) {
      apiClient<Array<{ member: { id: string; firstName: string; lastName: string } }>>(`/subcommittees/${subcommitteeId}/affiliations`)
        .then((data) => {
          if (Array.isArray(data)) {
            setSubcommitteeMembers(data.map((a) => a.member).filter(Boolean));
          }
        })
        .catch(() => setSubcommitteeMembers([]));
    }
  }

  async function handleSave() {
    if (!task || !id) return;
    setSaving(true);
    setError('');
    try {
      const payload: UpdateEventTaskPayload = {};
      if (title !== task.title) payload.title = title;
      if (description !== (task.description || '')) payload.description = description;
      if (status !== task.status) payload.status = status;
      if (priority !== task.priority) payload.priority = priority;
      if (category !== task.category) payload.category = category;
      if (dueDate !== (task.dueDate ? task.dueDate.split('T')[0] : '')) payload.dueDate = dueDate;
      if (eventId !== (task.event?.id || '')) payload.eventId = eventId || undefined;
      if (fulfillmentComments !== (task.fulfillmentComments || '')) payload.fulfillmentComments = fulfillmentComments;

      await updateTask(id, payload);
      await loadTask(id);
      setEditing(false);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!id || !task) return;
    if (!window.confirm(`Delete task "${task.title}"? This cannot be undone.`)) return;
    try {
      await deleteTask(id);
      navigate('/all-tasks');
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to delete task.');
    }
  }

  function handleCancel() {
    if (task) populateForm(task);
    setEditing(false);
    setError('');
  }

  // ── Assignee management ─────────────────────────────────────

  async function handleAddAssignee() {
    if (!addMemberId || !id) return;
    setAssigningMember(true);
    setError('');
    try {
      await apiClient(`/event-tasks/${id}/assignees`, {
        method: 'POST',
        body: { memberId: addMemberId, roleId: addRoleId || null },
      });
      await reloadAssignees();
      setAddMemberId('');
      setAddRoleId('');
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to assign member.');
    } finally {
      setAssigningMember(false);
    }
  }

  async function handleRemoveAssignee(memberId: string) {
    if (!id) return;
    try {
      await apiClient(`/event-tasks/${id}/assignees/${memberId}`, { method: 'DELETE' });
      setAssignees((prev) => prev.filter((a) => a.memberId !== memberId));
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to remove assignee.');
    }
  }

  async function handleRoleChange(memberId: string, roleId: string) {
    if (!id) return;
    try {
      await apiClient(`/event-tasks/${id}/assignees`, {
        method: 'POST',
        body: { memberId, roleId: roleId || null },
      });
      await reloadAssignees();
    } catch {
      // Silent — role update is non-critical
    }
  }

  async function reloadAssignees() {
    if (!id) return;
    try {
      const data = await getTask(id);
      setAssignees(data.assignees || []);
    } catch {
      // Non-critical
    }
  }

  function getAvailableMembers() {
    const assignedIds = new Set(assignees.map((a) => a.memberId));
    return subcommitteeMembers.filter((m) => !assignedIds.has(m.id));
  }

  // ── Render ──────────────────────────────────────────────────

  if (loading) {
    return <div style={styles.page}><div style={styles.loading}>Loading task...</div></div>;
  }

  if (!task) {
    return (
      <div style={styles.page}>
        {error && <div style={styles.error}>{error}</div>}
        <div style={styles.loading}>Task not found.</div>
        <button style={styles.backLink} onClick={() => navigate('/all-tasks')}>← Back to All Tasks</button>
      </div>
    );
  }

  const availableMembers = getAvailableMembers();

  return (
    <div style={styles.page}>
      {/* Back navigation */}
      <button style={styles.backLink} onClick={() => navigate('/all-tasks')} aria-label="Back to All Tasks">
        ← All Tasks
      </button>

      <div style={styles.card}>
        {error && <div style={styles.error}>{error}</div>}

        {/* Header */}
        <div style={styles.headerRow}>
          <div style={{ flex: 1 }}>
            {editing ? (
              <input
                style={{ ...styles.input, fontSize: '1.25rem', fontWeight: 600 }}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                aria-label="Task title"
              />
            ) : (
              <h1 style={styles.title}>{task.title}</h1>
            )}
          </div>
          {!editing && (
            <button style={styles.btnPrimary} onClick={() => setEditing(true)}>
              ✏️ Edit
            </button>
          )}
        </div>

        {/* Status & Priority badges (read mode) */}
        <div style={styles.metaRow}>
          <span style={{ ...styles.badge, backgroundColor: STATUS_COLORS[task.status] || '#6b7280' }}>
            {STATUS_LABELS[task.status] || task.status}
          </span>
          <span style={{ ...styles.badge, backgroundColor: PRIORITY_COLORS[task.priority] || '#6b7280' }}>
            {task.priority} Priority
          </span>
          <span style={{ ...styles.badge, backgroundColor: '#4a5568' }}>
            {task.category}
          </span>
          {task.completedAt && (
            <span style={styles.metaLabel}>Completed {formatShortDate(task.completedAt)}</span>
          )}
        </div>

        {/* ── Core Fields Section ─────────────────────────── */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Details</div>

          {editing ? (
            <>
              <div style={styles.fieldGrid}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Status</label>
                  <select style={styles.formSelect} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
                    <option value="ToDo">To Do</option>
                    <option value="InProgress">In Progress</option>
                    <option value="Done">Done</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Priority</label>
                  <select style={styles.formSelect} value={priority} onChange={(e) => setPriority(e.target.value)} aria-label="Priority">
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Category</label>
                  <select style={styles.formSelect} value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category">
                    <option value="Weekend">Weekend</option>
                    <option value="Planning">Planning</option>
                    <option value="General">General</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Due Date</label>
                  <input type="date" style={styles.input} value={dueDate} onChange={(e) => setDueDate(e.target.value)} aria-label="Due date" />
                </div>
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Event</label>
                <select style={styles.formSelect} value={eventId} onChange={(e) => setEventId(e.target.value)} aria-label="Event">
                  <option value="">— No Event —</option>
                  {events.map((evt) => (
                    <option key={evt.id} value={evt.id}>{evt.name}</option>
                  ))}
                </select>
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Description</label>
                <textarea
                  style={styles.textarea}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={2000}
                  aria-label="Task description"
                />
              </div>
            </>
          ) : (
            <>
              <div style={styles.fieldGrid}>
                <div style={styles.fieldGroup}>
                  <span style={styles.label}>Subcommittee</span>
                  <span style={styles.value}>{task.subcommittee?.name || '—'}</span>
                </div>
                <div style={styles.fieldGroup}>
                  <span style={styles.label}>Event</span>
                  <span style={styles.value}>{task.event?.name || '—'}</span>
                </div>
                <div style={styles.fieldGroup}>
                  <span style={styles.label}>Due Date</span>
                  <span style={styles.value}>{formatDate(task.dueDate)}</span>
                </div>
                <div style={styles.fieldGroup}>
                  <span style={styles.label}>Created By</span>
                  <span style={styles.value}>
                    {task.createdBy ? `${task.createdBy.firstName} ${task.createdBy.lastName}` : '—'}
                  </span>
                </div>
              </div>
              {task.description && (
                <div style={styles.fieldGroup}>
                  <span style={styles.label}>Description</span>
                  <p style={{ ...styles.value, whiteSpace: 'pre-wrap', margin: '0.25rem 0 0' }}>{task.description}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Assignees & Roles Section ───────────────────── */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Assignees & Roles</div>

          {assignees.length === 0 && !editing && (
            <p style={{ color: TEXT_MUTED, fontSize: '0.85rem', margin: 0 }}>No assignees assigned.</p>
          )}

          {assignees.map((a) => (
            <div key={a.id} style={styles.assigneeRow}>
              <span style={styles.assigneeName}>{a.member.firstName} {a.member.lastName}</span>
              {editing ? (
                <>
                  <select
                    style={styles.roleSelect}
                    value={a.roleId || ''}
                    onChange={(e) => handleRoleChange(a.memberId, e.target.value)}
                    aria-label={`Role for ${a.member.firstName} ${a.member.lastName}`}
                  >
                    <option value="">No role</option>
                    {weekendTaskRoles.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                  <button
                    style={styles.removeBtn}
                    onClick={() => handleRemoveAssignee(a.memberId)}
                    title="Remove assignee"
                    aria-label={`Remove ${a.member.firstName} ${a.member.lastName}`}
                  >
                    ✕
                  </button>
                </>
              ) : (
                <span style={{ fontSize: '0.8rem', color: TEXT_MUTED, fontStyle: 'italic' }}>
                  {a.role?.name || 'No role'}
                </span>
              )}
            </div>
          ))}

          {editing && availableMembers.length > 0 && (
            <div style={styles.addRow}>
              <select
                style={{ ...styles.formSelect, flex: 1, minWidth: '140px', maxWidth: '220px' }}
                value={addMemberId}
                onChange={(e) => setAddMemberId(e.target.value)}
                aria-label="Add assignee"
              >
                <option value="">— Add assignee —</option>
                {availableMembers.map((m) => (
                  <option key={m.id} value={m.id}>{m.firstName} {m.lastName}</option>
                ))}
              </select>
              <select
                style={{ ...styles.roleSelect, maxWidth: '160px' }}
                value={addRoleId}
                onChange={(e) => setAddRoleId(e.target.value)}
                aria-label="Role for new assignee"
              >
                <option value="">No role</option>
                {weekendTaskRoles.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
              <button
                style={styles.btnSecondary}
                onClick={handleAddAssignee}
                disabled={!addMemberId || assigningMember}
              >
                + Add
              </button>
            </div>
          )}
        </div>

        {/* ── Fulfillment Comments Section ────────────────── */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Fulfillment Comments</div>

          {editing ? (
            <textarea
              style={styles.textarea}
              value={fulfillmentComments}
              onChange={(e) => setFulfillmentComments(e.target.value)}
              placeholder="Add notes on task fulfillment, blockers, or completion details..."
              aria-label="Fulfillment comments"
            />
          ) : (
            <p style={{ ...styles.value, whiteSpace: 'pre-wrap', margin: 0, color: task.fulfillmentComments ? '#333' : TEXT_MUTED }}>
              {task.fulfillmentComments || 'No fulfillment comments yet.'}
            </p>
          )}
        </div>

        {/* ── Timestamps ──────────────────────────────────── */}
        <div style={styles.timestamps}>
          Created: {formatTimestamp(task.createdAt)} &bull; Updated: {formatTimestamp(task.updatedAt)}
        </div>

        {/* ── Action Buttons ──────────────────────────────── */}
        {editing && (
          <div style={styles.actions}>
            <button style={styles.btnDanger} onClick={handleDelete}>
              🗑️ Delete Task
            </button>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button style={styles.btnSecondary} onClick={handleCancel}>Cancel</button>
              <button style={styles.btnPrimary} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
